/* eslint-disable max-lines */
import { HIRING_RELIABILITY_CONFIG } from "@clipboard-health/contract-backend-main";
import { Card, Divider, HorizontalScrollView, Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, CssBaseline, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useScrollRestoration } from "@src/appV2/lib/utils/useScrollRestoration";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useCredentialRequirementConfiguration } from "@src/appV2/redesign/Documents/useCredentialRequirementConfiguration";
import { useCreateOrGetPlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import { useTrackMarketingLinkClicks } from "@src/appV2/redesign/PlacementCandidate/hooks/useTrackMarketingLinkClicks";
import {
  BOOKING_REORDERING_COHORT,
  PLACEMENT_COHORT,
  PlacementCandidateScreeningStatus,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { useCreateWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useCreateWorkerFavoritePlacement";
import { useDeleteWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useDeleteWorkerFavoritePlacement";
import { HighlightSection } from "@src/appV2/redesign/Placements/components/PlacementDetailsPage/components/HighlightSection";
import { PlacementDetailsPageHeader } from "@src/appV2/redesign/Placements/components/PlacementDetailsPage/components/PlacementDetailsPageHeader";
import {
  isPlacementDetailsSectionId,
  PLACEMENT_DETAILS_SECTION_IDS,
  REQUIREMENTS_SECTION_ID,
  WORKPLACE_INFO_SECTION_ID,
  WORKPLACE_LOCATION_SECTION_ID,
} from "@src/appV2/redesign/Placements/placementDetailsSections";
import {
  INTERVIEW_TYPE,
  InterviewStatus,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { addDays, addMinutes, setHours, setMilliseconds, setMinutes, setSeconds } from "date-fns";
import { isEmpty } from "lodash";
// eslint-disable-next-line no-restricted-imports
import Markdown from "markdown-to-jsx";
import { useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useGetIncentives } from "../../api/useGetIncentives";
import {
  type GetAvailabilityResponse,
  transformInterviewAvailabilityData,
  useGetInterviewAvailability,
} from "../../api/useGetInterviewAvailability";
import { transformPlacementData, useGetPlacement } from "../../api/useGetPlacement";
import { useBookingOrderingCohort } from "../../hooks/useBookingOrderingCohort";
import { useEasySchedulingState } from "../../hooks/useEasySchedulingState";
import { usePlacementCohorts } from "../../hooks/usePlacementCohorts";
import { RecruiterBookingFlowProvider } from "../../hooks/useRecruiterBookingFlowContext";
import { PlacementApplicationStatus } from "../../types/fetchPlacements.schema";
import { useHiringIncentiveProgram } from "../../useHiringIncentiveProgram";
import {
  hasAppliedToPlacement,
  hasAttendedInterview,
  hasBookedInterview,
  hasCancelledInterview,
  hasNoShowInterview,
  interviewRequired,
  isApplicationCompleted,
} from "../../utils";
import { getWorkplaceStartHour } from "../../utils/reliabilityRule";
import { PlacementApplicationMessage } from "../PlacementApplicationMessage";
import { PlacementApplyButtonContainer } from "../PlacementApplyButton/PlacementApplyButtonContainer";
import { PlacementCredentialRequirementsCard } from "../PlacementCredentialsRequirementsCard";
import { PlacementDetailsSkeleton } from "../PlacementDetailsSkeleton";
import { PlacementDocumentRequirementsCardsContainer } from "../PlacementDocumentRequirementsCardsContainer";
import { RestrictedAccessBanner } from "../RestrictedAccessBanner";
import { SignOnBonusBanner } from "../SignOnBonusBanner";
import { TryAShiftCard } from "../TryAShiftCard";
import { AlternativeAddressSection } from "./components/AlternativeAddressSection";
import {
  hasInterviewAvailabilityEnabled,
  InterviewAvailabilitySection,
} from "./components/InterviewAvailabilitySection";
import { PayAvailabilityDetailsSection } from "./components/PayAvailabilityDetailsSection";
import { PlacementScheduleSection } from "./components/PlacementScheduleSection";
import { PlacementTagsSection } from "./components/PlacementTagsSection";
import { ReliabilityRuleSection } from "./components/ReliabilityRuleSection";
import { ResponseTimeSection } from "./components/ResponseTimeSection";
import { ScheduleNotesSection } from "./components/ScheduleNotesSection";
import { SectionChips } from "./components/SectionChips";
import { WorkplaceInfoSection } from "./components/WorkplaceInfoSection";
import { WorkplaceLocationSection } from "./components/WorkplaceLocationSection";
import { WorkplacePhotosSection } from "./components/WorkplacePhotosSection";
import { WorkplaceReviewsSection } from "./components/WorkplaceReviewsSection";
import { useAlternativeAddress } from "./hooks/useAlternativeAddress";
import { usePayTransparency } from "./hooks/usePayTransparency";
import { scrollToSectionInContainer } from "./sectionNavigation";

/**
 * Rounds up to the nearest 15-minute interval (0, 15, 30, 45).
 *
 * @example
 * // roundUpToNearest15Minutes(new Date('2023-01-01 10:23')) → 2023-01-01 10:30
 * // roundUpToNearest15Minutes(new Date('2023-01-01 10:45')) → 2023-01-01 10:45
 * // roundUpToNearest15Minutes(new Date('2023-01-01 10:47')) → 2023-01-01 11:00
 */
function roundUpToNearest15Minutes(date: Date): Date {
  const currentMinutes = date.getMinutes();
  const remainder = currentMinutes % 15;

  const truncatedDate = setMilliseconds(setSeconds(date, 0), 0);

  if (remainder === 0) {
    return truncatedDate;
  }

  const minutesToAdd = 15 - remainder;
  return addMinutes(truncatedDate, minutesToAdd);
}

function scrollToSectionOutsideReviewMode(params: {
  scrollContainer: HTMLDivElement;
  sectionId: string;
}) {
  const { scrollContainer, sectionId } = params;
  const element = document.querySelector<HTMLElement>(`#${sectionId}`);

  if (!element) {
    return false;
  }

  const offsetPosition = element.getBoundingClientRect().top + window.scrollY - 50;

  scrollContainer.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });

  return true;
}

export function PlacementDetailsPageV2() {
  // <editor-fold desc="Hooks">
  const { placementId } = useParams<{
    placementId: string;
  }>();
  const { hiringIncentiveProgram } = useHiringIncentiveProgram();
  const worker = useDefinedWorker();
  const history = useHistory();

  // Handle optional URL parameter for scrolling to a section
  const scrollToSectionQueryParameter = new URLSearchParams(history.location.search).get("section");
  const scrollToSection =
    isDefined(scrollToSectionQueryParameter) &&
    isPlacementDetailsSectionId(scrollToSectionQueryParameter)
      ? scrollToSectionQueryParameter
      : undefined;

  const {
    data: placement,
    isLoading: isPlacementLoading,
    isSuccess: isPlacementSuccess,
    isFetching: isPlacementFetching,
    refetch: refetchPlacement,
  } = useGetPlacement(
    { placementId, workerId: worker.userId },
    { enabled: isDefined(placementId) && isDefined(worker.userId) }
  );

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();
  // Use placement data even during refetch to prevent dependent queries from disabling
  const placementDetails = placement
    ? transformPlacementData(placement, credentialRequirementConfiguration)
    : undefined;

  const {
    data: placementCandidate,
    isLoading: isPlacementCandidateLoading,
    isSuccess: isPlacementCandidateSuccess,
    refetch: refetchPlacementCandidate,
  } = useCreateOrGetPlacementCandidate(
    { workerId: worker.userId },
    { enabled: isDefined(worker.userId) }
  );
  const { data: signOnBonuses, isSuccess: isGetIncentivesSuccess } = useGetIncentives(
    {
      workerId: worker.userId,
      queryParams: {
        filter: { sourceType: "Placement" },
      },
    },
    { enabled: hiringIncentiveProgram.enabled }
  );

  const { transparencyPayRates, highestMatchingQualification, isPayTransparencyAvailable } =
    usePayTransparency(placementDetails);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Restore scroll position when navigating back from a requirement/document page.
  // The key includes placementId so different placements don't share scroll positions.
  // Skip saving when a section query parameter is present, as section-based scrolling takes priority.
  useScrollRestoration({
    key: `placement-details-scroll-position-${placementId}`,
    containerId: "placementDetailsScrollContainer",
    saveOnUnmount: !isDefined(scrollToSection),
  });

  const { mutateAsync: unfavoritePlacement, isLoading: isUnfavoritingPlacement } =
    useDeleteWorkerFavoritePlacement();

  const { mutateAsync: favoritePlacement, isLoading: isFavoritingPlacement } =
    useCreateWorkerFavoritePlacement();

  const { isEasyScheduling: initialIsEasyScheduling } = useEasySchedulingState({
    placementDetails,
    interviewAvailability: [],
  });
  const bookingReorderingCohort = useBookingOrderingCohort();
  const { cohorts, flexReviewsInJobs } = usePlacementCohorts();
  const { enabled: isFlexReviewsInJobsEnabled, minFlexRatingsCountToShow } = flexReviewsInJobs;
  const isFlexReviewsCohortEnabled =
    isFlexReviewsInJobsEnabled && cohorts.includes(PLACEMENT_COHORT.FLEX_REVIEWS_IN_JOBS);
  const { data: ratingAggregates } = useGetRatingAggregates(placementDetails?.workplace.id ?? "", {
    enabled: isFlexReviewsCohortEnabled && isDefined(placementDetails?.workplace.id),
  });
  const isReviewModeEnabled =
    isFlexReviewsCohortEnabled &&
    isDefined(ratingAggregates) &&
    ratingAggregates.numberOfRatings > minFlexRatingsCountToShow;

  const {
    data: interviewAvailabilityResponse,
    isInitialLoading: isLoadingInterviewAvailability,
    refetch: refetchInterviewAvailability,
  } = useGetInterviewAvailability(
    { workplaceId: placementDetails?.workplace.id ?? "" },
    {
      filter: {
        workerId: worker.userId,
        placementId,
        canScheduleInterviews:
          (placementDetails?.interviewSettings?.attributes?.scheduleInterviews ?? false) ||
          initialIsEasyScheduling,
        excludedInterviewId: placementDetails?.interviews
          .filter((interview) => interview.attributes.status === InterviewStatus.BOOKED)
          .map((interview) => interview.id)
          .join(","),
        ...(placementDetails?.interviewSettings?.attributes?.selfServeInterviewBookingType ===
          INTERVIEW_TYPE.PHONE_SCREEN && {
          start: {
            // Rounding is required because the server validates that dates are aligned to 15 minutes.
            gte: roundUpToNearest15Minutes(addMinutes(new Date(), 5)),
          },
        }),
        ...(bookingReorderingCohort === BOOKING_REORDERING_COHORT.BOOK_FIRST &&
          !placementCandidate?.screeningStatus?.startsWith(
            PlacementCandidateScreeningStatus.PASSED
          ) && {
            start: {
              // Next day at 9am if before noon
              // Next next day at 9am if after noon
              gte: setMilliseconds(
                setSeconds(
                  setMinutes(
                    setHours(addDays(new Date(), new Date().getHours() < 12 ? 1 : 2), 9),
                    0
                  ),
                  0
                ),
                0
              ),
            },
          }),
      },
    },
    {
      enabled:
        isDefined(placementDetails?.workplace.id) &&
        ((placementDetails?.interviewSettings?.attributes?.scheduleInterviews ?? false) ||
          initialIsEasyScheduling),
    }
  );

  // Cache interview availability to prevent data loss when query toggles between enabled/disabled
  // This happens when placement data temporarily becomes undefined or business logic changes
  const lastValidAvailabilityRef = useRef<
    | {
        workplaceId: string;
        placementId: string;
        data: GetAvailabilityResponse;
      }
    | undefined
  >(undefined);

  const currentWorkplaceId = placementDetails?.workplace.id;

  // Clear cache when navigating to a different workplace or placement
  if (
    currentWorkplaceId &&
    placementId &&
    (lastValidAvailabilityRef.current?.workplaceId !== currentWorkplaceId ||
      lastValidAvailabilityRef.current?.placementId !== placementId)
  ) {
    lastValidAvailabilityRef.current = undefined;
  }

  // Update cache with fresh data for the current placement
  if (interviewAvailabilityResponse && currentWorkplaceId && placementId) {
    lastValidAvailabilityRef.current = {
      workplaceId: currentWorkplaceId,
      placementId,
      data: interviewAvailabilityResponse,
    };
  }

  const interviewAvailability = placementDetails
    ? transformInterviewAvailabilityData(
        interviewAvailabilityResponse ?? lastValidAvailabilityRef.current?.data,
        worker.tmz,
        placementDetails
      )
    : [];

  const { isEasyScheduling, isEasySchedulingDeepLink } = useEasySchedulingState({
    placementDetails,
    interviewAvailability,
  });

  const { showAlternativeAddressInformation, showBookedInterviewLocation, alternativeAddressData } =
    useAlternativeAddress({
      interviewSettings: placementDetails?.interviewSettings,
      placementAddressFormatted: placementDetails?.address.formatted,
    });

  const isChildCareCenter = placementDetails?.workplace.type === "Child Care Center";

  const isLoading = isPlacementLoading || isPlacementCandidateLoading;
  const isSuccess = isPlacementSuccess && isPlacementCandidateSuccess;

  useTrackMarketingLinkClicks({
    placementCandidate,
    workplaceId: placementDetails?.workplace.id,
    placementId: placementDetails?.id,
    enabled:
      isDefined(placementCandidate) &&
      isDefined(placementDetails) &&
      isDefined(placementDetails.id) &&
      isDefined(placementDetails.workplace.id),
  });

  // Scroll to the requested section (if it exists) once the page data is ready
  useEffect(() => {
    const isValidSection = isReviewModeEnabled
      ? isDefined(scrollToSection) && PLACEMENT_DETAILS_SECTION_IDS.includes(scrollToSection)
      : scrollToSection === WORKPLACE_LOCATION_SECTION_ID;

    if (isPlacementSuccess && isPlacementCandidateSuccess && isValidSection) {
      // Use setTimeout to ensure DOM is fully rendered
      const timeoutId = setTimeout(() => {
        const scrollContainer = document.querySelector<HTMLDivElement>(
          `#placementDetailsScrollContainer`
        );

        if (!scrollContainer || !isDefined(scrollToSection)) {
          return;
        }

        const didScroll = isReviewModeEnabled
          ? scrollToSectionInContainer({
              scrollContainer,
              sectionId: scrollToSection,
            })
          : scrollToSectionOutsideReviewMode({
              scrollContainer,
              sectionId: scrollToSection,
            });

        if (!didScroll) {
          return;
        }

        // Clean up the section parameter from URL
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.delete("section");
        history.replace({
          search: searchParams.toString(),
        });

        logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
          action: "scroll_to_section",
          section: scrollToSection,
          placementId: placementDetails?.id,
          placementCandidateId: placementCandidate?.id,
          workerId: worker.userId,
        });
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return undefined;
  }, [
    isReviewModeEnabled,
    isPlacementSuccess,
    isPlacementCandidateSuccess,
    scrollToSection,
    history,
    placementDetails?.id,
    placementCandidate?.id,
    worker.userId,
  ]);
  // </editor-fold>

  const hasApplied = hasAppliedToPlacement(placementDetails);
  const requiredInterview = interviewRequired(placementDetails);
  const bookedInterview = hasBookedInterview(placementDetails);
  const attendedInterview = hasAttendedInterview(placementDetails);
  const noShowInterview = hasNoShowInterview(placementDetails);
  const bookedOrAttendedInterview = bookedInterview || attendedInterview;
  const isNoShowAndRestricted = placementCandidate?.isRestricted === true && noShowInterview;
  const applicationCompleted = isApplicationCompleted(placementDetails);
  const isSignOnBonusPaid = signOnBonuses?.data.some(
    (incentive) => incentive.attributes.status === "paid"
  );
  const cancelledInterview = hasCancelledInterview(placementDetails);
  const hasPendingInterviewRequests =
    isDefined(placementDetails?.applications?.[0]?.attributes.workerInterviewRequest) &&
    [PlacementApplicationStatus.APPLIED, PlacementApplicationStatus.ACCEPTED].includes(
      placementDetails?.applications?.[0]?.attributes.status
    ) &&
    placementDetails.applications[0].attributes.workerInterviewRequest?.dates.length > 0 &&
    !bookedInterview;
  const workplaceStartHour = placementDetails
    ? getWorkplaceStartHour({
        workerTimezone: worker.tmz,
        workplaceTimezone: placementDetails.workplace.tmz,
        startHour: HIRING_RELIABILITY_CONFIG.WORKPLACE_START_HOUR,
      })
    : undefined;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <CssBaseline />
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
          {placement && placementDetails && isSuccess && (
            <PlacementDetailsPageHeader
              placement={placementDetails}
              scrollContainerRef={scrollContainerRef}
              isUpdatingFavoritedStatus={
                isUnfavoritingPlacement || isFavoritingPlacement || isLoading
              }
              onFavoritePlacementChange={async () => {
                if (
                  !placementCandidate ||
                  isUnfavoritingPlacement ||
                  isFavoritingPlacement ||
                  isLoading ||
                  !isSuccess
                ) {
                  return;
                }

                if (placementDetails.isFavorited && placementDetails.workerFavoritePlacementId) {
                  await unfavoritePlacement({
                    workerId: worker.userId,
                    workerFavoritePlacementId: placementDetails.workerFavoritePlacementId,
                    placementId: placementDetails.id,
                    source: "detail-header",
                  });
                } else if (!placementDetails.isFavorited) {
                  await favoritePlacement({
                    workerId: worker.userId,
                    placementId: placementDetails.id,
                    source: "detail-header",
                  });
                }
              }}
            />
          )}
          {isReviewModeEnabled && isSuccess && placementDetails && placementCandidate && (
            <SectionChips
              scrollContainerRef={scrollContainerRef}
              credentialRequirements={placementDetails.credentialRequirements}
              workerRequirements={placementDetails.workerRequirements}
              placementId={placementDetails.id}
              placementCandidateId={placementCandidate.id}
              workerId={worker.userId}
            />
          )}
          <Box
            ref={scrollContainerRef}
            id="placementDetailsScrollContainer"
            sx={{ flex: 1, overflowY: "auto" }}
          >
            <Stack
              sx={{
                paddingX: 6,
                paddingBottom:
                  // TODO: how does `isDocumentRequirementValid` fit in here?
                  hasApplied || placementDetails?.isLicenseEligible !== false ? "200px" : "250px",
                height: "fit-content",
              }}
              flex={1}
              spacing={5}
            >
              {isLoading && <PlacementDetailsSkeleton />}

              {isSuccess && placementDetails && (
                <Stack spacing={6}>
                  {isReviewModeEnabled ? (
                    // Padding is needed to account for the sticky section chips
                    <Divider color="primary" sx={{ paddingTop: 4 }} />
                  ) : (
                    <Divider color="primary" />
                  )}

                  {placementCandidate.isRestricted && placementCandidate.mostRecentBan ? (
                    <RestrictedAccessBanner
                      placementCandidateBan={placementCandidate.mostRecentBan}
                      workerTimezone={worker.tmz}
                    />
                  ) : undefined}

                  <Box id={WORKPLACE_INFO_SECTION_ID}>
                    <WorkplaceInfoSection
                      isReviewModeEnabled={isReviewModeEnabled}
                      ratingAggregates={ratingAggregates}
                      placement={placementDetails}
                      placementCandidate={placementCandidate}
                    />
                  </Box>

                  <WorkplacePhotosSection
                    photos={placementDetails.workplace.photos ?? []}
                    placementId={placementDetails.id}
                    placementCandidateId={placementCandidate.id}
                    workerId={worker.userId}
                  />

                  {isReviewModeEnabled && (
                    <WorkplaceReviewsSection
                      workplaceId={placementDetails.workplace.id}
                      placementId={placementDetails.id}
                    />
                  )}

                  {/* To show when applied and application is not completed and either interview is required or interview has been booked */}
                  {hasApplied &&
                    !applicationCompleted &&
                    (!requiredInterview ||
                      bookedOrAttendedInterview ||
                      isNoShowAndRestricted ||
                      cancelledInterview) && (
                      <>
                        <Card outlined variant="primary">
                          <CardContent>
                            <PlacementApplicationMessage
                              interviews={placementDetails.interviews}
                              workerTimezone={worker.tmz}
                              hasPendingInterviewRequests={hasPendingInterviewRequests}
                              applicationStatus={
                                placementDetails.applications?.[0]?.attributes.status
                              }
                              workplaceName={placementDetails.workplace.name}
                              placementId={placementDetails.id}
                              workplaceId={placementDetails.workplace.id}
                              interviewInstructions={placementDetails?.interviewInstructions}
                              hasActiveBan={
                                placementCandidate.isRestricted === true &&
                                isDefined(placementCandidate.mostRecentBan)
                              }
                              interviewType={
                                placementDetails.interviewSettings?.attributes
                                  .selfServeInterviewBookingType
                              }
                            />
                          </CardContent>
                        </Card>
                        {isDefined(workplaceStartHour) &&
                        placementDetails.interviewSettings?.attributes.scheduleInterviews &&
                        placementDetails.interviewSettings?.attributes
                          .selfServeInterviewBookingType !== INTERVIEW_TYPE.PHONE_SCREEN ? (
                          <ReliabilityRuleSection workplaceStartHour={workplaceStartHour} />
                        ) : undefined}
                      </>
                    )}

                  {placementDetails?.applications?.[0]?.attributes.workerVoiceNoteUrl && (
                    <Text variant="caption">
                      <Text variant="body2">Your voice note</Text>
                      <audio
                        controls
                        src={placementDetails.applications[0].attributes.workerVoiceNoteUrl}
                      >
                        <track kind="captions" />
                      </audio>
                    </Text>
                  )}

                  {hasApplied &&
                    showBookedInterviewLocation &&
                    isDefined(alternativeAddressData) && (
                      <AlternativeAddressSection
                        workerId={worker.userId}
                        placementId={placementDetails.id}
                        placementCandidateId={placementCandidate.id}
                        alternativeAddressData={alternativeAddressData}
                      />
                    )}

                  <PayAvailabilityDetailsSection
                    placement={{
                      ...placementDetails,
                    }}
                    placementCandidate={placementCandidate}
                    isPayTransparencyAvailable={isPayTransparencyAvailable}
                    highestMatchingQualification={highestMatchingQualification}
                    transparencyPayRates={transparencyPayRates}
                  />

                  {!isPayTransparencyAvailable && !isChildCareCenter && <Divider color="primary" />}
                  {!isChildCareCenter && <PlacementScheduleSection placement={placementDetails} />}

                  {placementDetails.schedule && placementDetails.schedule.trim() !== "" && (
                    <>
                      <Divider color="primary" />
                      <ScheduleNotesSection scheduleNotes={placementDetails.schedule} />
                    </>
                  )}

                  {isDefined(placementDetails.tags) && placementDetails.tags.length > 0 && (
                    <PlacementTagsSection placement={placementDetails} />
                  )}

                  {(!isEmpty(placementDetails.perks) ||
                    isDefined(placementDetails.signOnBonus)) && (
                    <>
                      <Divider color="primary" />

                      <HighlightSection
                        signOnBonus={placementDetails.signOnBonus}
                        perks={placementDetails.perks ?? []}
                      />
                    </>
                  )}

                  {hasInterviewAvailabilityEnabled(placementDetails) && (
                    <>
                      <Divider color="primary" />
                      <InterviewAvailabilitySection
                        placement={placementDetails}
                        placementCandidate={placementCandidate}
                      />
                    </>
                  )}

                  <Divider color="primary" />
                  <ResponseTimeSection
                    placement={placementDetails}
                    placementCandidate={placementCandidate}
                  />

                  <Divider id={WORKPLACE_LOCATION_SECTION_ID} color="primary" />
                  <WorkplaceLocationSection
                    placement={placementDetails}
                    placementCandidateId={placementCandidate.id}
                  />

                  {showAlternativeAddressInformation && isDefined(alternativeAddressData) && (
                    <Stack>
                      <Markdown>{alternativeAddressData.description}</Markdown>
                    </Stack>
                  )}

                  <Box id={REQUIREMENTS_SECTION_ID} sx={{ marginTop: 4 }}>
                    <HorizontalScrollView stretch>
                      <PlacementCredentialRequirementsCard placementDetails={placementDetails} />
                      <PlacementDocumentRequirementsCardsContainer
                        placementDetails={placementDetails}
                      />
                    </HorizontalScrollView>
                  </Box>

                  <TryAShiftCard
                    placementDetails={placementDetails}
                    placementCandidate={placementCandidate}
                  />

                  {hiringIncentiveProgram.enabled &&
                  !isSignOnBonusPaid &&
                  isGetIncentivesSuccess &&
                  !placementCandidate.isRestricted ? (
                    <SignOnBonusBanner
                      placementId={placementDetails.id}
                      placementCandidate={placementCandidate}
                    />
                  ) : undefined}
                </Stack>
              )}
            </Stack>
            {isSuccess && placementDetails && placementCandidate && (
              <RecruiterBookingFlowProvider
                placementCandidate={placementCandidate}
                source="detail-cta"
                placement={placementDetails}
              >
                <PlacementApplyButtonContainer
                  placementDetails={placementDetails}
                  interviewAvailability={interviewAvailability}
                  placementCandidate={placementCandidate}
                  refetchPlacement={refetchPlacement}
                  isRefetchingPlacement={isPlacementFetching}
                  isLoadingInterviewAvailability={isLoadingInterviewAvailability}
                  refetchInterviewAvailability={refetchInterviewAvailability}
                  refetchPlacementCandidate={refetchPlacementCandidate}
                  showAlternativeAddressInformation={showAlternativeAddressInformation}
                  alternativeAddressData={alternativeAddressData}
                  isEasySchedulingDeepLink={isEasySchedulingDeepLink}
                  isEasySchedulingPlacement={isEasyScheduling}
                />
              </RecruiterBookingFlowProvider>
            )}
          </Box>
        </Box>
      </PageWrapper>
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
